import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../store/hooks';
import { selectUser } from '../store/slices/authSlice';
import {
  DEFAULT_WORKSPACE,
  STORAGE_KEYS,
  isValidWorkspace,
  isNavVisibleForWorkspace,
} from '../config/workspaceExperience';
import { persistWorkspacePreferences } from '../utils/workspacePreferences';
import { ONBOARDING_STORAGE_KEY } from '../config/onboardingConfig';

const WorkspaceExperienceContext = createContext(null);

function readStoredExperience() {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.experience);
    return isValidWorkspace(v) ? v : DEFAULT_WORKSPACE;
  } catch {
    return DEFAULT_WORKSPACE;
  }
}

function readShowDeveloperTools() {
  try {
    return localStorage.getItem(STORAGE_KEYS.showDeveloperTools) === 'true';
  } catch {
    return false;
  }
}

/** Write preferences to localStorage only — no events (avoids update loops). */
function applyPreferencesToStorage(preferences) {
  if (!preferences || typeof preferences !== 'object') return;

  const { workspaceExperience, showDeveloperTools, onboardingComplete } = preferences;

  if (workspaceExperience && isValidWorkspace(workspaceExperience)) {
    localStorage.setItem(STORAGE_KEYS.experience, workspaceExperience);
  }
  if (typeof showDeveloperTools === 'boolean') {
    localStorage.setItem(STORAGE_KEYS.showDeveloperTools, showDeveloperTools ? 'true' : 'false');
  }
  if (onboardingComplete === true) {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
  }
}

export function WorkspaceExperienceProvider({ children }) {
  const user = useAppSelector(selectUser);
  const [workspace, setWorkspaceState] = useState(readStoredExperience);
  const [showDeveloperTools, setShowDeveloperToolsState] = useState(readShowDeveloperTools);
  const hydratedUserIdRef = useRef(null);

  const serverWorkspace = user?.preferences?.workspaceExperience;
  const serverDevTools = user?.preferences?.showDeveloperTools;

  useEffect(() => {
    if (!user?.id) {
      hydratedUserIdRef.current = null;
      return;
    }

    if (hydratedUserIdRef.current === user.id) return;
    hydratedUserIdRef.current = user.id;

    if (user.preferences) {
      applyPreferencesToStorage(user.preferences);
    }

    const storedWorkspace = readStoredExperience();
    const storedDevTools = readShowDeveloperTools();

    setWorkspaceState((prev) => (prev === storedWorkspace ? prev : storedWorkspace));
    setShowDeveloperToolsState((prev) => (prev === storedDevTools ? prev : storedDevTools));
  }, [user?.id, serverWorkspace, serverDevTools]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEYS.experience && e.newValue && isValidWorkspace(e.newValue)) {
        setWorkspaceState((prev) => (prev === e.newValue ? prev : e.newValue));
      }
      if (e.key === STORAGE_KEYS.showDeveloperTools) {
        const next = e.newValue === 'true';
        setShowDeveloperToolsState((prev) => (prev === next ? prev : next));
      }
    };
    const onWorkspaceChange = () => {
      const nextWs = readStoredExperience();
      const nextDev = readShowDeveloperTools();
      setWorkspaceState((prev) => (prev === nextWs ? prev : nextWs));
      setShowDeveloperToolsState((prev) => (prev === nextDev ? prev : nextDev));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('aariyatalk-workspace-change', onWorkspaceChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('aariyatalk-workspace-change', onWorkspaceChange);
    };
  }, []);

  const setWorkspace = useCallback(
    (mode) => {
      if (!isValidWorkspace(mode)) return;
      localStorage.setItem(STORAGE_KEYS.experience, mode);
      setWorkspaceState((prev) => (prev === mode ? prev : mode));
      window.dispatchEvent(new Event('aariyatalk-workspace-change'));
      if (user) {
        persistWorkspacePreferences({ workspaceExperience: mode });
      }
    },
    [user]
  );

  const setShowDeveloperTools = useCallback(
    (enabled) => {
      const flag = Boolean(enabled);
      localStorage.setItem(STORAGE_KEYS.showDeveloperTools, flag ? 'true' : 'false');
      setShowDeveloperToolsState((prev) => (prev === flag ? prev : flag));
      window.dispatchEvent(new Event('aariyatalk-workspace-change'));
      if (user) {
        persistWorkspacePreferences({ showDeveloperTools: flag });
      }
    },
    [user]
  );

  const isNavVisible = useCallback(
    (navId) => isNavVisibleForWorkspace(navId, workspace, showDeveloperTools),
    [workspace, showDeveloperTools]
  );

  const value = useMemo(
    () => ({
      workspace,
      setWorkspace,
      showDeveloperTools,
      setShowDeveloperTools,
      isNavVisible,
      isBuilder: workspace === 'builder',
      isBusiness: workspace === 'business',
      isAgency: workspace === 'agency',
      isEnterprise: workspace === 'enterprise',
    }),
    [workspace, showDeveloperTools, setWorkspace, setShowDeveloperTools, isNavVisible]
  );

  return (
    <WorkspaceExperienceContext.Provider value={value}>{children}</WorkspaceExperienceContext.Provider>
  );
}

export function useWorkspaceExperience() {
  const ctx = useContext(WorkspaceExperienceContext);
  if (!ctx) {
    throw new Error('useWorkspaceExperience must be used within WorkspaceExperienceProvider');
  }
  return ctx;
}

export default useWorkspaceExperience;
